import React, { useEffect, useRef } from 'react'
import { notification, Button } from 'antd'
import { SettingOutlined } from '@ant-design/icons'

const NOTIFICATION_ENDPOINT =
  process.env.REACT_APP_NOTIFICATION_ENDPOINT || 'ws://localhost:8085'

const EVENT_TITLES = {
  parking: 'จอดในจุดห้ามจอด',
  speed_over: 'ความเร็วเกินกำหนด',
  driving_4hr: 'ขับต่อเนื่อง > 4 ชม.',
  driving_10hr: 'ปฏิบัติงาน > 10 ชม./วัน',
  aggressive: 'ออกตัวกระชาก/เบรคกระทันหัน',
}

const loadSettingsFromStorage = () => {
  try {
    const raw = localStorage.getItem('notification-settings')
    if (raw) return JSON.parse(raw)
  } catch (_e) {
    // ignore
  }
  return null
}

const fetchSettingsFromApi = accessToken => {
  const url = `${process.env.REACT_APP_LARAVEL_API_ENDPOINT}/user-notification-setting`
  return fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
    .then(res => res.json())
    .then(json => {
      if (json.status === 200 && json.data && json.data.settings) {
        localStorage.setItem(
          'notification-settings',
          JSON.stringify(json.data.settings),
        )
        return json.data.settings
      }
      return null
    })
    .catch(() => null)
}

const isEventEnabled = (settings, eventType) => {
  if (!settings) return false
  return settings[eventType] !== false
}

const logEventView = (data, token) => {
  const httpUrl = NOTIFICATION_ENDPOINT.replace(/^ws(s?)/, 'http$1')
  fetch(`${httpUrl}/api/event-view-log`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      event_id: data.eventId || '',
      event_type: data.eventType,
      vehicle_id: data.vehicleId,
      action: 'view',
    }),
  }).catch(() => {})
}

const NotificationProvider = ({ children }) => {
  const settingsRef = useRef(loadSettingsFromStorage())

  useEffect(() => {
    let ws
    let reconnectTimer

    const audio = new Audio('/audio/notification/slow-spring-board.mp3')

    // Fetch settings once on mount, not on every reconnect
    const initialToken = localStorage.getItem('access-token')
    if (initialToken) {
      fetchSettingsFromApi(initialToken).then(settings => {
        if (settings) settingsRef.current = settings
      })
    }

    const connect = () => {
      const accessToken = localStorage.getItem('access-token')
      if (!accessToken) {
        reconnectTimer = setTimeout(connect, 5000)
        return
      }

      let vehicleVisibility = ''
      try {
        const profile = JSON.parse(localStorage.getItem('profile'))
        vehicleVisibility = (profile && profile.vehicle_visibility) || ''
      } catch (e) {
        // ignore parse error
      }

      const wsUrl = NOTIFICATION_ENDPOINT.replace(/^http(s?)/, 'ws$1')

      ws = new WebSocket(`${wsUrl}/ws?token=${accessToken}`)

      ws.onopen = () => {
        console.log('notification ws connected')
        ws.send(
          JSON.stringify({
            type: 'init',
            vehicle_visibility: vehicleVisibility,
          }),
        )
      }

      ws.onmessage = event => {
        const data = JSON.parse(event.data)
        if (data.type === 'safety-event') {
          // Always dispatch event for popover (backend filters there)
          window.dispatchEvent(
            new CustomEvent('safety-event-received', { detail: data }),
          )

          // Check if this event type is enabled for popup notifications
          if (!isEventEnabled(settingsRef.current, data.eventType)) {
            return
          }

          if (!settingsRef.current || settingsRef.current.sound !== false) {
            audio.currentTime = 0
            audio.play().catch(() => {})
          }

          const title = EVENT_TITLES[data.eventType] || 'Safety Event'

          let timeStr = ''
          if (data.startAt) {
            const d = new Date(data.startAt)
            timeStr = d.toLocaleString('th-TH', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          }

          let desc = (
            <div>
              <div>
                {data.plateNo
                  ? `${data.plateNo} ${
                      data.vehicleCode ? `(${data.vehicleCode})` : ''
                    }`
                  : data.title || ''}
              </div>
              {data.eventType === 'speed_over' && (
                <div style={{ fontSize: 12, color: '#808080' }}>
                  {data.avgValue &&
                    ` ความเร็ว ${parseFloat(data.avgValue).toFixed(
                      1,
                    )} km/h`}{' '}
                  . {timeStr && timeStr}
                </div>
              )}
            </div>
          )

          const key = `noti_${Date.now()}`
          notification.open({
            key,
            message: `${title}`,
            description: desc,
            placement: 'topRight',
            duration: 15,
            top: 56,
            btn: (
              <Button
                type="link"
                size="small"
                style={{ color: '#808080' }}
                onClick={() => {
                  notification.close(key)
                  window.open('/notification-setting', '_blank')
                }}
              >
                <SettingOutlined />
              </Button>
            ),
            onClick: () => logEventView(data, accessToken),
          })
        }
      }

      ws.onclose = e => {
        console.log('notification ws disconnected', e.code)
        reconnectTimer = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        // onclose will fire after this
      }
    }

    connect()

    // Listen for settings changes from the settings page
    const handleSettingsChanged = () => {
      settingsRef.current = loadSettingsFromStorage()
    }
    window.addEventListener(
      'notification-settings-changed',
      handleSettingsChanged,
    )

    return () => {
      clearTimeout(reconnectTimer)
      if (ws) ws.close()
      window.removeEventListener(
        'notification-settings-changed',
        handleSettingsChanged,
      )
    }
  }, [])

  return children
}

export default NotificationProvider
